<template>
    <v-card outlined class="mb-3">
        <v-card-title class="pb-2">
            {{ $t('approval.shopping_cart_no', {n: index}) }}
            <v-spacer/>
            <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                    <v-btn
                        @click="onDeleteOrder"
                        v-on="on"
                        :disabled="disableDeleteOrder"
                        color="error"
                        icon
                    >
                        <v-icon>mdi-trash-can</v-icon>
                    </v-btn>
                </template>
                <span>{{ $t('tooltip.order.delete') }}</span>
            </v-tooltip>
        </v-card-title>
        <v-card-text>
            <!-- add product -->
            <h3 class="grey--text mb-3">{{ $t('approval.add_product_quickly') }}</h3>
            <v-autocomplete
                v-model="autocompleteProduct"
                :items="autocompleteProducts"
                :loading="isBusyAutocompleteProducts > 0"
                :search-input.sync="keywordsProducts"
                prepend-icon="mdi-magnify"
                :label="$t('approval.product')"
                :hint="$t('approval.start_typing_any_product_attribute')"
                item-text="name"
                class="mb-3"
                persistent-hint
                hide-no-data
                return-object
                clearable
                outlined
                dense
                no-filter
                @change="onAddProduct"
            >
                <template v-slot:item="{ item }">
                    <v-list-item-avatar tile>
                        <v-img :src="item.image_url"/>
                    </v-list-item-avatar>
                    <v-list-item-content>
                        <v-list-item-title>
                            {{ item.name }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                            {{ item.model }}
                        </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                        {{ item.in_stock }}
                        {{ $t('products.pieces') }}
                    </v-list-item-action>
                </template>
            </v-autocomplete>
            <!-- table of products -->
            <h3 class="grey--text mb-3">{{ $t('approval.products_in_order') }}</h3>
            <v-card class="mb-3" outlined>
                <v-data-table
                    class="elevation-0"
                    :headers="productsTableHeaders"
                    :items="products"
                    :items-per-page="15"
                    :footer-props="dataTableFooterProps"
                    :search="productsTableSearch"
                    :custom-filter="dataTableCustomFilter"
                    :loading-text="$t('common.loading_table_data')"
                >
                    <template v-slot:top>
                        <v-toolbar flat color="white">
                            <v-text-field
                                v-model="productsTableSearch"
                                prepend-inner-icon="mdi-magnify"
                                :placeholder="$t('common.filter')"
                                outlined
                                dense
                                hide-details
                            />
                        </v-toolbar>
                    </template>
                    <template v-slot:item.thumbnail="{ item }">
                        <v-avatar size="50" tile>
                            <v-img :src="item.image_url"/>
                        </v-avatar>
                    </template>
                    <template v-slot:item.in_stock="{ item }">
                        {{ item.in_stock }}
                        {{ $t("products.pieces") }}
                    </template>
                    <template v-slot:item.quantity="{ item }">
                        <div style="max-width: 100px">
                            <v-text-field
                                type="number"
                                :value="item.quantity"
                                :error="item.quantity > item.in_stock || item.quantity < 1"
                                dense
                                hide-details
                                outlined
                                @change="onProductQuantityChange($event, item)"
                                @input="onProductQuantityChange($event, item)"
                            />
                        </div>
                    </template>
                    <template v-slot:item.actions="{ item }">
                        <div class="text-no-wrap">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                        @click="onDeleteProduct(item)"
                                        v-on="on"
                                        :disabled="disableDeleteProduct"
                                        color="error"
                                        icon
                                    >
                                        <v-icon>mdi-trash-can</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ $t('tooltip.product.delete') }}</span>
                            </v-tooltip>
                        </div>
                    </template>
                </v-data-table>
            </v-card>
            <!-- add recipient -->
            <h3 class="grey--text mb-3">{{ $t('approval.add_recipient') }}</h3>
            <v-autocomplete
                v-model="autocompleteRecipient"
                :items="autocompleteRecipients"
                :loading="isBusyAutocompleteRecipients > 0"
                :search-input.sync="keywordsRecipients"
                prepend-icon="mdi-notebook"
                :label="$t('approval.recipient')"
                :hint="$t('approval.start_typing_any_recipient_attribute')"
                item-value="address_external_id"
                item-text="address_external_id"
                class="mb-3"
                persistent-hint
                hide-no-data
                return-object
                clearable
                outlined
                dense
                no-filter
                @change="onAddRecipient"
            >
                <template v-slot:prepend-item>
                    <div class="mt-1 mx-3 mb-3">
                        <v-btn color="secondary" outlined small block @click="onAddAllRecipients">
                            <v-icon left>mdi-account-multiple-plus</v-icon>
                            {{ $t('shopping_cart.add_all_recipients') }}
                        </v-btn>
                    </div>
                    <v-divider/>
                </template>
                <template v-slot:item="{ item }">
                    {{ addressString(item) }}
                </template>
            </v-autocomplete>
            <!-- table of recipients -->
            <h3 class="grey--text mb-3">{{ $t('approval.recipients') }}</h3>
            <v-card outlined>
                <v-data-table
                    class="elevation-0"
                    :headers="recipientsTableHeaders"
                    :items="recipients"
                    :items-per-page="15"
                    :footer-props="dataTableFooterProps"
                    :search="recipientsTableSearch"
                    :custom-filter="dataTableCustomFilter"
                    :loading-text="$t('common.loading_table_data')"
                >
                    <template v-slot:top>
                        <v-toolbar flat color="white">
                            <v-text-field
                                v-model="recipientsTableSearch"
                                prepend-inner-icon="mdi-magnify"
                                :placeholder="$t('common.filter')"
                                outlined
                                dense
                                hide-details
                            />
                        </v-toolbar>
                    </template>
                    <template v-slot:item.address_string="{ item }">
                        {{ item.address_string }}
                    </template>
                    <template v-slot:item.email="{ item }">
                        <v-text-field
                            type="email"
                            v-model="item.email"
                            :error="!item.email"
                            outlined
                            dense
                            single-line
                            hide-details
                            class="my-1"
                            @input="onRecipientChange(item)"
                        />
                    </template>
                    <template v-slot:item.phone="{ item }">
                        <v-text-field
                            type="email"
                            v-model="item.phone"
                            :error="!item.phone"
                            outlined
                            dense
                            single-line
                            hide-details
                            class="my-1"
                            @input="onRecipientChange(item)"
                        />
                    </template>
                    <template v-slot:item.actions="{ item }">
                        <span class="text-no-wrap">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                        @click="onDeleteRecipient(item)"
                                        v-on="on"
                                        :disabled="disableDeleteRecipient"
                                        color="error"
                                        icon
                                    >
                                        <v-icon>
                                            mdi-trash-can
                                        </v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ $t('tooltip.recipients.delete') }}</span>
                            </v-tooltip>
                        </span>
                    </template>
                </v-data-table>
            </v-card>
        </v-card-text>
    </v-card>
</template>

<script>
import {API} from "@/api/api";
import SnackbarMixin from "@/mixins/SnackbarMixin";
import DataTableMixin from "@/mixins/DataTableMixin";
import _ from "lodash";

export default {
    name: "EditOrderInBulkOrder",
    mixins: [SnackbarMixin, DataTableMixin],
    props: {
        index: {
            type: Number,
            required: true
        },
        userId: {
            type: Number,
            required: true
        },
        warehouseId: {
            type: Number,
            required: true
        },
        totalNumberOfOrders: {
            type: Number,
            required: true
        }
    },
    data: () => ({
        // loading
        isBusyAutocompleteProducts: 0,
        isBusyAutocompleteRecipients: 0,
        // product autocomplete
        // -- model
        autocompleteProduct: null,
        // -- value
        keywordsProducts: null,
        // -- list of products to add
        autocompleteProducts: [],
        // -- request counter
        productsRequestCounter: 0,
        // recipient autocomplete
        // -- model
        autocompleteRecipient: null,
        // -- value
        keywordsRecipients: null,
        // -- list of recipients to add
        autocompleteRecipients: [],
        // -- request counter
        recipientsRequestCounter: 0,
        // table filters
        productsTableSearch: "",
        recipientsTableSearch: ""
    }),
    watch: {
        keywordsProducts: _.debounce(function (keywords) {
            if (!keywords || keywords.length < 1) {
                return;
            }

            const url = "/api/warehouses/" + encodeURI(this.warehouseId) + "/products"
                + "?page=1"
                + "&perPage=100"
                + "&filter=" + encodeURI(keywords);

            let requestNumber = (this.productsRequestCounter += 1);

            this.isBusyAutocompleteProducts += 1;
            API.get(url)
                .then(response => {
                    if (requestNumber === this.productsRequestCounter) {
                        this.autocompleteProducts = response.data;
                    }
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusyAutocompleteProducts -= 1;
                });
        }, 250),
        keywordsRecipients: _.debounce(function (keywords) {
            if (!keywords || keywords.length < 1) {
                return;
            }

            const url = "/api/addresses/by-warehouse/" + encodeURI(this.warehouseId)
                + "?page=1"
                + "&perPage=100"
                + "&filter=" + encodeURI(keywords);

            let requestNumber = (this.recipientsRequestCounter += 1);

            this.isBusyAutocompleteRecipients += 1;
            API.get(url)
                .then(response => {
                    if (requestNumber === this.recipientsRequestCounter) {
                        this.autocompleteRecipients = response.data;
                    }
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusyAutocompleteRecipients -= 1;
                });
        }, 250)
    },
    computed: {
        productsTableHeaders() {
            return [
                {
                    text: this.$t('approval.thumbnail'),
                    value: 'thumbnail',
                    sortable: false
                },
                {
                    text: this.$t('approval.name'),
                    value: 'name',
                    sortable: true
                },
                {
                    text: this.$t('approval.model'),
                    value: 'model',
                    sortable: true
                },
                {
                    text: this.$t('approval.in_stock'),
                    value: 'in_stock',
                    sortable: false,
                },
                {
                    text: this.$t('approval.quantity'),
                    value: 'quantity',
                    sortable: false,
                },
                {
                    text: '',
                    value: 'actions',
                    sortable: false,
                    align: 'end'
                }
            ];
        },
        recipientsTableHeaders() {
            return [
                {
                    text: this.$t('recipients.recipient'),
                    value: 'address_string'
                },
                {
                    text: this.$t('recipients.email'),
                    value: 'email'
                },
                {
                    text: this.$t('recipients.phone'),
                    value: 'phone'
                },
                {
                    text: '',
                    value: 'actions',
                    align: 'end',
                    sortable: false
                }
            ];
        },
        products() {
            return this.$store.getters["editBulkOrder/getProducts"]({
                userId: this.userId,
                warehouseId: this.warehouseId,
                index: this.index
            });
        },
        recipients() {
            return this.$store.getters["editBulkOrder/getRecipientsOfOrder"]({
                userId: this.userId,
                warehouseId: this.warehouseId,
                index: this.index
            });
        },
        disableDeleteRecipient() {
            return this.recipients.length <= 1
        },
        disableDeleteProduct() {
            return this.products.length <= 1
        },
        disableDeleteOrder() {
            return this.totalNumberOfOrders <= 1;
        }
    },
    methods: {
        onDeleteOrder(withConfirmation = true) {
            if (withConfirmation) {
                if (!confirm(this.$t('confirm.delete_order_from_bulk_order'))) {
                    return;
                }
            }
            this.$store.commit("editBulkOrder/deleteOrder", {
                userId: this.userId,
                warehouseId: this.warehouseId,
                index: this.index
            })
        },
        onAddProduct() {
            if (!this.autocompleteProduct) {
                return;
            }

            this.$store.commit("editBulkOrder/addProduct", {
                userId: this.userId,
                warehouseId: this.warehouseId,
                index: this.index,
                product: this.autocompleteProduct
            });

            this.autocompleteProduct = null;
            this.keywordsProducts = null;
        },
        onAddAllRecipients() {
            const addresses = this.autocompleteRecipients.filter(address => this.filterRecipients(address, this.keywordsRecipients));
            addresses.forEach(address => this.storeRecipient(address));
            this.autocompleteRecipient = null;
            this.keywordsRecipients = null;
        },
        onAddRecipient() {
            if (!this.autocompleteRecipient) {
                return;
            }
            this.storeRecipient(this.autocompleteRecipient);
            this.autocompleteRecipient = null;
            this.keywordsRecipients = null;
        },
        onProductQuantityChange(quantity, product) {
            this.$store.commit("editBulkOrder/changeProductQuantity", {
                userId: this.userId,
                warehouseId: this.warehouseId,
                index: this.index,
                product: product,
                quantity: quantity
            });
        },
        onRecipientChange(recipient) {
            this.$store.commit("editBulkOrder/changeRecipient", {
                userId: this.userId,
                warehouseId: this.warehouseId,
                index: this.index,
                recipient: recipient
            })
        },
        onDeleteProduct(product, withConfirmation = true) {
            if (withConfirmation) {
                if (!confirm(this.$t('confirm.delete_product_from_order', {product: product.name}))) {
                    return;
                }
            }
            this.$store.commit("editBulkOrder/removeProduct", {
                userId: this.userId,
                warehouseId: this.warehouseId,
                index: this.index,
                product: product
            })
        },
        onDeleteRecipient(recipient, withConfirmation = true) {
            if (withConfirmation) {
                if (!confirm(this.$t('confirm.delete_recipient_from_order'))) {
                    return;
                }
            }
            this.$store.commit("editBulkOrder/removeRecipient", {
                userId: this.userId,
                warehouseId: this.warehouseId,
                index: this.index,
                recipient: recipient
            });
        },
        addressString(address) {
            let addressFields = [
                ("#" + address.address_external_id).trim(),
                ("" + address.name).trim(),
                ("" + address.ico).trim(),
                ("" + address.dic).trim(),
                ("" + address.street + " " + address.house_number).trim(),
                ("" + address.suburb).trim(),
                ("" + address.postal_code).trim(),
                ("" + address.country).trim()
            ];
            addressFields = addressFields.filter(x => x && x !== 'null');
            return addressFields.join(", ");
        },
        storeRecipient(address) {
            // get recipient's contact info from previous orders (from same bulk order)
            const recipient = this.$store.getters["editBulkOrder/getRecipientsOfBulkOrder"]({
                userId: this.userId,
                warehouseId: this.warehouseId
            }).find(r => r.address_id === address.id)

            let data = {
                // recipient's address
                address_id: address.address_external_id,
                buyer_id: address.external_buyer_id,
                // all recipient's address fields as single string (to display them in table only)
                address_string: [
                    ("#" + address.address_external_id).trim(),
                    ("" + address.name).trim(),
                    ("" + address.ico).trim(),
                    ("" + address.dic).trim(),
                    ("" + address.street + " " + address.house_number).trim(),
                    ("" + address.suburb).trim(),
                    ("" + address.postal_code).trim(),
                    ("" + address.country).trim()
                ].filter(x => x && x !== 'null').join(", "),
                // recipient's contact
                email: recipient ? recipient.email : address.email,
                phone: recipient ? recipient.phone : address.phone,
            };

            this.$store.commit("editBulkOrder/addRecipient", {
                userId: this.userId,
                warehouseId: this.warehouseId,
                index: this.index,
                recipient: data
            })
        },
        filterRecipients(address, query) {
            if (!("" + query.trim())) {
                return true;
            }
            let keywords = query.toLowerCase().trim().split(' ');
            let addressFields = [
                address.id ? ("#" + address.id) : null,
                address.name ? address.name.toLowerCase().trim() : null,
                address.ico ? address.ico.toLowerCase().trim() : null,
                address.dic ? address.dic.toLowerCase().trim() : null,
                address.phone ? address.phone.toLowerCase().trim() : null,
                address.email ? address.email.toLowerCase().trim() : null,
                address.street ? address.street.toLowerCase().trim() : null,
                address.house_number ? address.house_number.toLowerCase().trim() : null,
                address.suburb ? address.suburb.toLowerCase().trim() : null,
                address.city ? address.city.toLowerCase().trim() : null,
                address.postal_code ? address.postal_code.toLowerCase().trim() : null,
                address.country ? address.country.toLowerCase().trim() : null,
                address.country_iso_code ? address.country_iso_code.toLowerCase().trim() : null,
            ];
            addressFields = addressFields.filter(x => x && x !== 'null');

            for (let i = 0; i < keywords.length; i++) {
                let match = false;
                let keyword = keywords[i];
                for (let j = 0; j < addressFields.length; j++) {
                    let addressField = addressFields[j];
                    if (addressField.includes(keyword)) {
                        match = true;
                        break;
                    }
                }
                if (match) {
                    return true;
                }
            }

            return false;
        }
    }
}
</script>
